/**
 * Spec 022: the guest pickup catalog grouped by state, then city, then location,
 * plus search. Pure rules, mirrored by Flutter `pickup_location_groups.dart`;
 * both are checked against the same fixture (FR-012).
 */
import { stateOrProvinceName as usStateName } from './regions';

/** The fields of a `locations` / `getAllowedLocations.toLocation` row the picker reads. */
export interface PickupCatalogLocation {
  id: number;
  name?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  locationType?: string | null;
  airportCode?: string | null;
}

export interface PickupCityGroup<T extends PickupCatalogLocation> {
  key: string;
  name: string;
  locations: T[];
}

export interface PickupStateGroup<T extends PickupCatalogLocation> {
  key: string;
  name: string;
  locationCount: number;
  cities: PickupCityGroup<T>[];
}

export interface PickupPath {
  stateKey: string;
  cityKey: string | null;
}

export const OTHER_STATE = 'Other';
export const UNKNOWN_CITY = 'Unknown city';

const clean = (value: string | null | undefined): string => (value ?? '').trim();
const lower = (value: string | null | undefined): string => clean(value).toLowerCase();
const isUnknown = (value: string | null | undefined): boolean =>
  !clean(value) || lower(value) === 'unknown';
const compare = (a: string, b: string): number => a.localeCompare(b, 'en', { sensitivity: 'base' });

export const isAirport = (location: PickupCatalogLocation): boolean =>
  (location.locationType ?? '').toUpperCase() === 'AIRPORT';

/** Full name for a known US code, the stored text otherwise, "Other" when empty. */
export const stateDisplayName = (state: string | null | undefined): string =>
  isUnknown(state) ? OTHER_STATE : (usStateName(state) ?? clean(state));

/** 0 known US state, 1 other text, 2 "Other". */
const stateRank = (state: string | null | undefined): number =>
  isUnknown(state) ? 2 : usStateName(state) ? 0 : 1;

export const cityDisplayName = (city: string | null | undefined): string =>
  isUnknown(city) ? UNKNOWN_CITY : clean(city);

export const stateKeyOf = (location: PickupCatalogLocation): string =>
  stateDisplayName(location.state).toLowerCase();

export const cityKeyOf = (location: PickupCatalogLocation): string =>
  cityDisplayName(location.city).toLowerCase();

const hasOwnName = (location: PickupCatalogLocation): boolean =>
  !!clean(location.name) && lower(location.name) !== lower(location.address);

/** First line of a row: "SFO · San Francisco International Airport", the name, else the street. */
export const locationPrimaryLabel = (location: PickupCatalogLocation): string => {
  if (isAirport(location)) {
    const name = clean(location.name) || clean(location.address);
    const code = clean(location.airportCode).toUpperCase();
    return code ? `${code} · ${name}` : name;
  }
  return hasOwnName(location) ? clean(location.name) : clean(location.address);
};

/** Second line: the street when the first line is a name, then city, state and ZIP. */
export const locationSecondaryLabel = (location: PickupCatalogLocation): string => {
  const cityState = [cityDisplayName(location.city), clean(location.state)]
    .filter(Boolean)
    .join(', ');
  if (isAirport(location)) return cityState;
  const place = [cityState, clean(location.zipCode)].filter(Boolean).join(' ');
  return [hasOwnName(location) ? clean(location.address) : '', place].filter(Boolean).join(', ');
};

const sortKey = (location: PickupCatalogLocation): string =>
  clean(location.name) || clean(location.address);

const sortLocations = <T extends PickupCatalogLocation>(locations: T[]): T[] =>
  [...locations].sort(
    (a, b) => (isAirport(a) ? 0 : 1) - (isAirport(b) ? 0 : 1) || compare(sortKey(a), sortKey(b)),
  );

/** FR-007 to FR-010: states, then cities, then locations, with counts. */
export function groupPickupLocations<T extends PickupCatalogLocation>(
  locations: readonly T[],
): PickupStateGroup<T>[] {
  const states = new Map<
    string,
    { name: string; rank: number; cities: Map<string, PickupCityGroup<T>> }
  >();
  for (const location of locations) {
    const stateKey = stateKeyOf(location);
    let state = states.get(stateKey);
    if (!state) {
      state = {
        name: stateDisplayName(location.state),
        rank: stateRank(location.state),
        cities: new Map(),
      };
      states.set(stateKey, state);
    }
    const cityKey = cityKeyOf(location);
    let city = state.cities.get(cityKey);
    if (!city) {
      city = { key: cityKey, name: cityDisplayName(location.city), locations: [] };
      state.cities.set(cityKey, city);
    }
    city.locations.push(location);
  }
  return Array.from(states.entries())
    .sort(([, a], [, b]) => a.rank - b.rank || compare(a.name, b.name))
    .map(([key, state]) => {
      const cities = Array.from(state.cities.values())
        .sort((a, b) => compare(a.name, b.name))
        .map(city => ({ ...city, locations: sortLocations(city.locations) }));
      return {
        key,
        name: state.name,
        locationCount: cities.reduce((total, city) => total + city.locations.length, 0),
        cities,
      };
    });
}

/** The matched text a search hit can show; used to highlight. */
const searchFields = (location: PickupCatalogLocation): string[] => [
  stateDisplayName(location.state),
  cityDisplayName(location.city),
  clean(location.address),
  clean(location.name),
  clean(location.zipCode),
  clean(location.airportCode),
];

/**
 * FR-022: trimmed, case-insensitive. A state code matches only when the whole
 * query is the code ("ca" must not find "Biscayne"); everything else matches by
 * "contains". Results keep the group order.
 */
export function searchPickupLocations<T extends PickupCatalogLocation>(
  locations: readonly T[],
  query: string,
): T[] {
  const q = lower(query);
  if (!q) return [];
  const order = new Map<number, number>();
  groupPickupLocations(locations).forEach(state =>
    state.cities.forEach(city =>
      city.locations.forEach(location => order.set(location.id, order.size)),
    ),
  );
  return locations
    .filter(
      location =>
        lower(location.state) === q ||
        searchFields(location).some(field => !!field && field.toLowerCase().includes(q)),
    )
    .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
}

/** Where a location sits in the groups, or null when it is not in them. */
export function pickupPathOf<T extends PickupCatalogLocation>(
  groups: readonly PickupStateGroup<T>[],
  id: number | null | undefined,
): PickupPath | null {
  if (id == null) return null;
  for (const state of groups) {
    for (const city of state.cities) {
      if (city.locations.some(location => location.id === id)) {
        return { stateKey: state.key, cityKey: city.key };
      }
    }
  }
  return null;
}

/**
 * FR-019: where the return picker opens without a chosen return location: the
 * pickup's state and city, the state alone when that city has no return
 * option (or its only city when it has one), else the state list.
 */
export function anchorPathOf<T extends PickupCatalogLocation>(
  groups: readonly PickupStateGroup<T>[],
  anchor: PickupCatalogLocation | null | undefined,
): PickupPath | null {
  if (!anchor) return null;
  const state = groups.find(group => group.key === stateKeyOf(anchor));
  if (!state) return null;
  const cityKey = cityKeyOf(anchor);
  if (state.cities.some(city => city.key === cityKey)) return { stateKey: state.key, cityKey };
  return { stateKey: state.key, cityKey: state.cities.length === 1 ? state.cities[0].key : null };
}

/** FR-011: a state with one city opens that city. */
export const cityKeyAfterChoosing = <T extends PickupCatalogLocation>(
  state: PickupStateGroup<T> | undefined,
): string | null => (state && state.cities.length === 1 ? state.cities[0].key : null);

/** "1 location" / "4 locations". */
export const locationCountLabel = (count: number): string =>
  count === 1 ? '1 location' : `${count} locations`;
